<script>
import moment from 'moment'
import ImgUser from '@/assets/img/user.svg'
import ImgMail from '@/assets/img/mail.svg'
import ImgPhone from '@/assets/img/phone.svg'
import ImgCalendar from '@/assets/img/calendar.svg'
import UserResource from '@/resources/user'

const DATE_FORMAT = 'DD/MM/YYYY HH:mm'

const ACTION_CONFIG = {
  'birthday_client': { label: 'Anniversaire Client', cssClass: 'dashboard-action--birthday' },
  'anniversaire_paysage': { label: 'Anniversaire Paysage Patrimonial', cssClass: 'dashboard-action--paysage' },
  'renouvellement': { label: 'Renouvellement', cssClass: 'dashboard-action--renouvellement' },
  'versement_conditionnel': { label: 'Versement conditionnel', cssClass: 'dashboard-action--versement' },
  'versement_complementaire': { label: 'Versement complémentaire', cssClass: 'dashboard-action--versement' }
}

const API_KEY_MAPPING = {
  'birthday_client': 'birthday_client',
  'birthdate_pp': 'anniversaire_paysage',
  'renouvellement': 'renouvellement'
}

export default {
  name: 'CardClient',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      imgUser: ImgUser,
      imgMail: ImgMail,
      imgPhone: ImgPhone,
      imgCalendar: ImgCalendar,
      renewalLoading: false
    }
  },
  computed: {
    clientName () {
      return this.user.fullName || 'Client'
    },
    clientEmail () {
      return this.user.email || ''
    },
    clientPhone () {
      return this.user.phone || ''
    },
    clientStatus () {
      return this.user.status || 'Client'
    },
    actions () {
      const raw = this.user.actions
      if (!raw || typeof raw !== 'object') return []
      const today = moment().startOf('day')
      return Object.entries(raw)
        .filter(([, date]) => date)
        .map(([key, date]) => {
          const anniversary = this.getNextAnniversary(date, today)
          return {
            type: API_KEY_MAPPING[key] || key,
            date: anniversary.format('YYYY-MM-DD'),
            daysUntil: anniversary.diff(today, 'days')
          }
        })
    },
    daysSinceCreation () {
      if (!this.user.created_at) return 0
      const createdDate = moment(this.user.created_at, DATE_FORMAT)
      const days = moment().diff(createdDate, 'days')
      return this.formatDuration(days)
    },
    // Date exacte de création (traçabilité RGPD)
    creationDate () {
      if (!this.user.created_at) return null
      return moment(this.user.created_at, DATE_FORMAT).format('DD/MM/YYYY')
    }
  },
  methods: {
    getNextAnniversary (date, today) {
      let anniversary = moment(date, DATE_FORMAT).year(today.year())
      if (anniversary.isBefore(today, 'day')) {
        const nextYear = moment(date, DATE_FORMAT).year(today.year() + 1)
        if (nextYear.diff(today, 'days') < today.diff(anniversary, 'days')) {
          anniversary = nextYear
        }
      }
      return anniversary
    },
    formatDuration (days) {
      if (days < 1) return "aujourd'hui"
      if (days === 1) return '1 jour'
      if (days < 31) return days + ' jours'
      const months = Math.floor(days / 30)
      if (months === 1) return '1 mois'
      if (months < 12) return months + ' mois'
      const years = Math.floor(months / 12)
      if (years === 1) return '1 an'
      return years + ' ans'
    },
    formatDaysText (days) {
      if (days === 0) return "Aujourd'hui"
      const prefix = days < 0 ? 'il y a ' : 'dans '
      return prefix + this.formatDuration(Math.abs(days))
    },
    getActionClass (action) {
      const config = ACTION_CONFIG[action.type]
      return config ? config.cssClass : 'dashboard-action--default'
    },
    getActionLabel (action) {
      const config = ACTION_CONFIG[action.type]
      return config ? config.label : action.type
    },
    onRenewalCheck () {
      this.renewalLoading = true
      UserResource.updateContractRenewal(this.user.id)
        .then(() => {
          this.$emit('renewal-updated', this.user.id)
        })
        .catch(() => {
          this.$notify({ type: 'error', text: 'Erreur lors de la mise à jour du renouvellement' })
        })
        .finally(() => {
          this.renewalLoading = false
        })
    }
  }
}
</script>

<template>
	<article class="card-client dashboard-card dashboard-card--bordered dashboard-card--hoverable">
		<header class="dashboard-card__header">
			<img class="dashboard-card__icon" :src="imgUser" alt="">
			<div class="dashboard-card__header-info">
				<h4 class="dashboard-card__name">{{ clientName }}</h4>
				<span class="dashboard-status dashboard-status--client">{{ clientStatus }}</span>
			</div>
			<router-link :to="{ name: 'Client', params: { clientId: user.id } }">
				<svg class="dashboard-card__arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
					<path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</router-link>
		</header>

		<section class="dashboard-card__contact">
			<a v-if="clientEmail" :href="`mailto:${clientEmail}`" class="dashboard-card__contact-link">
				<img :src="imgMail" alt=""> {{ clientEmail }}
			</a>
			<a v-if="clientPhone" :href="`tel:${clientPhone}`" class="dashboard-card__contact-link">
				<img :src="imgPhone" alt=""> {{ clientPhone }}
			</a>
			<div class="dashboard-info-row">
				<img :src="imgCalendar" alt="" />
				Client depuis {{ daysSinceCreation }}<template v-if="creationDate"> (le {{ creationDate }})</template>
			</div>
		</section>

		<footer v-if="actions && actions.length > 0" class="card-client__actions">
			<div
				v-for="(action, index) in actions"
				:key="index"
				class="dashboard-action"
				:class="[getActionClass(action), { 'dashboard-action--warning': action.daysUntil < 0 }]"
			>
				<img :src="imgCalendar" alt="" class="dashboard-action__icon">
				<div class="dashboard-action__content">
					<p class="dashboard-action__title">
						{{ getActionLabel(action) }}
						<span v-if="action.daysUntil < 0" class="dashboard-action__warning-badge">En retard</span>
					</p>
					<p v-if="action.date" class="dashboard-action__date">
						{{ formatDaysText(action.daysUntil) }}
					</p>
				</div>
				<label v-if="action.type === 'renouvellement'" class="dashboard-action__checkbox" @click.stop>
					<input type="checkbox" :disabled="renewalLoading" @change="onRenewalCheck(action)">
					<span class="dashboard-action__checkbox-label">Fait</span>
				</label>
			</div>
		</footer>
	</article>
</template>

<style scoped>
.card-client__actions {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	border-top: 1px solid #E5E7EB;
	margin-top: 1rem;
	padding-top: 1rem;
}

.dashboard-action--birthday {
	border-left: 3px solid #9C27B0;
	background-color: rgba(156, 39, 176, 0.05);
}

.dashboard-action--paysage {
	border-left: 3px solid #B8860B;
	background-color: rgba(184, 134, 11, 0.05);
}

.dashboard-action--renouvellement {
	border-left: 3px solid #1976D2;
	background-color: rgba(25, 118, 210, 0.05);
}

.dashboard-action--versement {
	border-left: 3px solid #C62828;
	background-color: rgba(198, 40, 40, 0.05);
}

.dashboard-action__warning-badge {
	display: inline-block;
	font-size: 0.65rem;
	font-weight: 600;
	color: #fff;
	background-color: #ff0000;
	padding: 1px 6px;
	border-radius: 3px;
	margin-left: 0.4rem;
	vertical-align: middle;
}

.dashboard-action__checkbox {
	display: flex;
	align-items: center;
	gap: 0.3rem;
	margin-left: auto;
	cursor: pointer;
	white-space: nowrap;
}

.dashboard-action__checkbox input {
	cursor: pointer;
	width: 16px;
	height: 16px;
	accent-color: #1976D2;
}

.dashboard-action__checkbox-label {
	font-size: 0.75rem;
	color: #6B7280;
}
</style>



// WEBPACK FOOTER //
// CardClient.vue?d368a87a